import { isNullOrUndefined } from 'rewire-common';
import { observable } from 'rewire-core';
import { IToggleMenuItem } from 'rewire-ui';
import { RouteManagerBase } from '../main-navigation/RouteManager';
import { capitalize } from '../utility/StringUtils';
import { getGroupingItemIcon } from './PriorityGroupMenu';

export type TApplyFn = () => void;
export type TTGetGroupMenuItemsFn = () => IPriorityGroupMenuItem[];
export type PriorityGroupMode = 'single' | 'multiple';

export interface IPriorityGroupMenuItem {
  name: string;
  title: string;
  visible?: boolean | (() => boolean);
  disabled?: boolean | (() => boolean);
  isCustom?: boolean;
}

interface IPriorityGroupMenuState {
  mode: PriorityGroupMode;
  groups: string[];
}

export default class PriorityGroupMenuModel {
  state: IPriorityGroupMenuState;

  constructor(protected _apply: TApplyFn, public _getGroupMenuItems: TTGetGroupMenuItemsFn, protected _maxSelected: number | undefined = 4, public title = 'Group By:') {
    this.state = observable({
      mode: 'multiple',
      groups: [] as string[],
    });
  }

  load(groups?: string[], mode?: PriorityGroupMode) {
    this.mode = mode ?? 'multiple';
    this.groups = groups ?? [];
  }

  get mode(): PriorityGroupMode {
    return this.state.mode;
  }

  set mode(value: PriorityGroupMode) {
    this.state.mode = value;
  }

  get isSingleSelectMode(): boolean {
    return this.mode === 'single';
  }

  getGroupMenuItems() {
    return this._getGroupMenuItems().map(i => {
      return (
        Object.assign({}, i, {
          active: true,
          toggleIcon: getGroupingItemIcon(this, i.name),
        })
      );
    });
  }

  get groups(): string[] {
    return this.state.groups;
  }

  set groups(value: string[]) {
    if (!isNullOrUndefined(this.maxSelected)) {
      this.state.groups = value.slice(0, this.maxSelected);
    } else {
      this.state.groups = value;
    }
  }

  get hasGroups(): boolean {
    return this.numOfGroups > 0;
  }

  get numOfGroups(): number {
    return this.groups.length;
  }

  get maxSelected(): number | undefined {
    return this._maxSelected;
  }

  getGroupPriority(group: string): number | undefined {
    const priority = this.groups.findIndex(g => g === group) + 1;
    if (priority <= 0) {
      return undefined;
    }

    if (this.isSingleSelectMode) {
      return priority === 1 ? priority : undefined;
    }
    return priority;
  }

  handleItemClick = (item: IToggleMenuItem) => {
    if (this.mode === 'single') {
      this.handleSingleItemClick(item);
    } else {
      // multiple
      this.handleMultipleItemClick(item);
    }
  };

  handleSingleItemClick = (item: IToggleMenuItem) => {
    if (this.groups.includes(item.name)) {
      this.groups = [];
    } else {
      this.groups = [item.name];
    }

    this._apply();
  };

  handleMultipleItemClick = (item: IToggleMenuItem) => {
    const itemIdx = this.groups.findIndex((group) => group === item.name);
    if (itemIdx >= 0) {
      this.groups.splice(itemIdx, 1);
    } else if (this.canAddMoreGroups()) {
      this.groups.push(item.name);
    } else {
      this.groups.splice(this.numOfGroups - 1, 1, item.name);
    }

    this.groups = [...this.groups];
    this._apply();
  };

  canAddMoreGroups(): boolean {
    return isNullOrUndefined(this.maxSelected) || this.numOfGroups < this.maxSelected;
  }

  getGroupTooltip(): string {
    return this.getGroupsAsString();
  }

  getGroupsAsString(maxCount?: number): string {
    if (this.groups.length === 0) {
      return `${this.title} «none»`;
    }

    const groupNamesArr = this.groups.map(group => {
      if (group === 'generator') {
        return 'Shift Pattern';
      }
      return capitalize(group);
    });

    if (!isNullOrUndefined(maxCount) && groupNamesArr.length > maxCount) {
      groupNamesArr.splice(maxCount, groupNamesArr.length - maxCount);
      groupNamesArr.push('....');
    }

    return `${this.title} ${groupNamesArr.join(', ')}`;
  }
}

export class PermittedPriorityGroupMenuModel extends PriorityGroupMenuModel {
  constructor(_apply: TApplyFn, _getGroupMenuItems: TTGetGroupMenuItemsFn, _maxSelected?: number, title?: string) {
    super(_apply, _getGroupMenuItems, _maxSelected, title);
  }

  getGroupPriority(group: string): number | undefined {
    const groupPriority = super.getGroupPriority(group);
    if (!groupPriority) {
      return undefined;
    }
    let permittedGroupPriority = groupPriority;
    for (const innerGroup of this.groups) {
      const innerGroupPriority = super.getGroupPriority(innerGroup);
      if (innerGroupPriority && innerGroupPriority < groupPriority && !RouteManagerBase.current.permittedGroups.includes(innerGroup)) {
        permittedGroupPriority--;
      }
    }

    if (permittedGroupPriority <= 0) {
      return undefined;
    }

    return permittedGroupPriority;
  }
}